'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useState } from 'react';
import { Radar, ResponsiveContainer, RadarChart as RadarChart$1, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend } from 'recharts';
import { createVarsResolver, getThemeColor, factory, useProps, useMantineTheme, useStyles, useResolvedStylesApi, Box } from '@mantine/core';
import { ChartLegend } from '../ChartLegend/ChartLegend.mjs';
import classes from './RadarChart.module.css.mjs';

const defaultProps = {
  withPolarGrid: true,
  withPolarAngleAxis: true,
  withPolarRadiusAxis: false
};
const varsResolver = createVarsResolver((theme, { gridColor, textColor }) => ({
  root: {
    "--chart-grid-color": gridColor ? getThemeColor(gridColor, theme) : void 0,
    "--chart-text-color": textColor ? getThemeColor(textColor, theme) : void 0
  }
}));
const RadarChart = factory((_props, ref) => {
  const props = useProps("RadarChart", defaultProps, _props);
  const {
    classNames,
    className,
    style,
    styles,
    unstyled,
    vars,
    data,
    series,
    dataKey,
    gridColor,
    textColor,
    radarProps,
    radarChartProps,
    polarGridProps,
    polarAngleAxisProps,
    polarRadiusAxisProps,
    withPolarGrid,
    withPolarAngleAxis,
    withPolarRadiusAxis,
    children,
    withLegend,
    legendProps,
    ...others
  } = props;
  const theme = useMantineTheme();
  const getStyles = useStyles({
    name: "RadarChart",
    classes,
    props,
    className,
    style,
    classNames,
    styles,
    unstyled,
    vars,
    varsResolver
  });
  const [highlightedArea, setHighlightedArea] = useState(null);
  const { resolvedClassNames, resolvedStyles } = useResolvedStylesApi({
    classNames,
    styles,
    props
  });
  const radars = series.map((item, index) => /* @__PURE__ */ jsx(
    Radar,
    {
      name: item.name,
      dataKey: item.name,
      stroke: getThemeColor(item.strokeColor || item.color, theme),
      fill: getThemeColor(item.color, theme),
      fillOpacity: highlightedArea ? highlightedArea === item.name ? item.opacity || 0.4 : 0.05 : item.opacity || 0.4,
      strokeOpacity: highlightedArea ? highlightedArea === item.name ? 1 : 0.1 : 1,
      isAnimationActive: false,
      ...typeof radarProps === "function" ? radarProps(item) : radarProps
    },
    index
  ));
  return /* @__PURE__ */ jsx(Box, { ref, ...getStyles("root"), ...others, children: /* @__PURE__ */ jsx(ResponsiveContainer, { ...getStyles("container"), children: /* @__PURE__ */ jsxs(RadarChart$1, { data, ...radarChartProps, children: [
    withPolarGrid && /* @__PURE__ */ jsx(PolarGrid, { stroke: "var(--chart-grid-color)", ...polarGridProps }),
    withPolarAngleAxis && /* @__PURE__ */ jsx(PolarAngleAxis, { dataKey, ...polarAngleAxisProps }),
    withPolarRadiusAxis && /* @__PURE__ */ jsx(PolarRadiusAxis, { stroke: "var(--chart-grid-color)", ...polarRadiusAxisProps }),
    radars,
    withLegend && /* @__PURE__ */ jsx(
      Legend,
      {
        verticalAlign: "bottom",
        content: (payload) => /* @__PURE__ */ jsx(
          ChartLegend,
          {
            payload: payload.payload,
            onHighlight: setHighlightedArea,
            legendPosition: legendProps?.verticalAlign || "bottom",
            classNames: resolvedClassNames,
            styles: resolvedStyles,
            series,
            centered: true
          }
        ),
        ...legendProps
      }
    ),
    children
  ] }) }) });
});
RadarChart.displayName = "@mantine/charts/RadarChart";
RadarChart.classes = classes;

export { RadarChart };
//# sourceMappingURL=RadarChart.mjs.map
